import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Fab
} from '@mui/material';
import {
  LibraryBooks as LibraryBooksIcon,
  Label as LabelIcon,
  Build as BuildIcon,
  Dashboard as DashboardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Inventory as InventoryIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';


import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

import { db } from '../firebase';  // Your Firebase configuration file
import { doc, onSnapshot, setDoc } from 'firebase/firestore';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export default function ProjectsPage() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [apiLoading, setApiLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('normal'); // 'normal' veya 'Archived'
  const [remainingQuota, setRemainingQuota] = useState(null);
  const [organisation, setOrganisation] = useState('');
  const [codeBookLibrary, setCodeBookLibrary] = useState(null);

  const [masterAnchorEl, setMasterAnchorEl] = useState(null);
  const [masterSelectionData, setMasterSelectionData] = useState(null);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [brandAlgorithm, setBrandAlgorithm] = useState(false);

  const [selectedCodebookId, setSelectedCodebookId] = useState('');
  const [scanWithAI, setScanWithAI] = useState(false);

  const showLoading = () => { setApiLoading(true); };
  const hideLoading = () => { setApiLoading(false); };

  // Proje verilerini çek
  const fetchProjectData = async () => {
    if (!currentUser?.email) return;

    const formData = new FormData();
    formData.append('useremail', currentUser.email);
    try {
      const response = await fetch("https://codini-project-3-gt-795950092325.europe-west3.run.app", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (data && data.result) {
        setProjects(data.result);
        setCodeBookLibrary(data.codeBookLibrary);
        setRemainingQuota(data.remaining_quota);
        setOrganisation(data.organisation);
        const initialExpanded = {};
        data.result.forEach(project => {
          initialExpanded[project.projectCode] = true;
        });
        setExpandedGroups(initialExpanded);
      } else {
        console.error(t('projectPage.noData'));
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
      setSnackbar({
        open: true,
        message: t('projectPage.fetchError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (!currentUser?.email) return;
  
    const unsubscribe = onSnapshot(doc(db, 'codiniUpdates', currentUser.email), (doc) => {
      if (doc.exists() && doc.data().status === 'Completed') {
        fetchProjectData();  // Refresh project data
        // Optionally reset the status
        setDoc(doc.ref, { status: 'Completed' }, { merge: true });
      }
    });
  
    return () => unsubscribe();
  }, [currentUser]);



  const getFile = (projectID) => {
    showLoading();
    const formData = new FormData();
    formData.append('projectID', projectID);
    fetch("https://codini-project-5-7kpxqstxwq-ey.a.run.app", {
      method: "POST",
      body: formData
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(t('networkError'));
        }
        return response.json();
      })
      .then(data => {
        if (data.signed_url) {
          const signedUrl = data.signed_url;
          const downloadLink = document.createElement('a');
          downloadLink.href = signedUrl;
          downloadLink.download = 'Result.xlsx';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        } else {
          throw new Error(t('noSignedURL'));
        }
      })
      .catch(error => {
        console.error('Error:', error);
        setSnackbar({
          open: true,
          message: t('failedToRetrieveURL'),
          severity: 'error',
        });
      })
      .finally(() => {
        hideLoading();
      });
  };

  const getFinalFile = (projectID) => {
    showLoading();
    const formData = new FormData();
    formData.append('projectID', projectID);
    fetch("https://codini-project-8-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(t('networkError'));
        }
        return response.json();
      })
      .then(data => {
        if (data.merged_excel_url) {
          const signedUrl = data.merged_excel_url;
          const downloadLink = document.createElement('a');
          downloadLink.href = signedUrl;
          downloadLink.download = 'Result.xlsx';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        } else {
          throw new Error(t('noMergedURL'));
        }
      })
      .catch(error => {
        console.error('Error:', error);
        setSnackbar({
          open: true,
          message: t('failedToRetrieveURL'),
          severity: 'error',
        });
      })
      .finally(() => {
        hideLoading();
      });
  };

  const archiveProject = async (projectID, status) => {
    // Onay diyaloğu
    let message = "";
    if (status === 'Archived' || status === 'Deleted') {
      if (status === 'Archived') {
        message = t('confirmArchive');
      } else if (status === 'Deleted') {
        message = t('confirmDelete');
      }
      if (!window.confirm(message)) {
        return;
      }
    }

    showLoading();
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('status', status);

    try {
      const response = await fetch("https://europe-west3-evosurvey.cloudfunctions.net/codini-project-archive", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.result) {
        setSnackbar({
          open: true,
          message: t('updateSuccess'),
          severity: 'success',
        });
        await fetchProjectData();
      } else {
        throw new Error(data.message || t('updateError'));
      }
    } catch (error) {
      console.error(t('project.updateError'), error);
      setSnackbar({
        open: true,
        message: t('updateError'),
        severity: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  const sendResult = (projectID, codeName, dataCount, question, masterExist) => {
    showLoading();
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);
    fetch("https://codini-project-datadashboard-get-7kpxqstxwq-ey.a.run.app", {
      method: "POST",
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        hideLoading();
        let dashboardData, dashboardCodebook, AI_Report;
        if (data.noDashboardData === "ok") {
          dashboardData = null;
          dashboardCodebook = null;
          AI_Report = null;
        } else {
          dashboardData = data.result_data;
          dashboardCodebook = data.codebook_data;
          AI_Report = data.AI_Report;
        }
        navigate('/dashboard', {
          state: {
            dashboardData,
            dashboardCodebook,
            projectID,
            codeName,
            dataCount,
            question,
            masterExist,
            AI_Report,
            codeBookLibrary
          },
        });
      })
      .catch(error => {
        console.error(t('sendResult.error'), error);
        hideLoading();
      });
  };

  const sendCodebook = (projectID, codeName, dataCount, question) => {
    showLoading();
    const formData = new FormData();
    const paramaters_online = "fromncategori"
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);
    fetch("https://codini-project-1-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        hideLoading();
        navigate('/category', {
          state: {
            updatedCategoryList: data.updated_category_list,
            projectID,
            codeName,
            dataCount,
            question,
            remainingQuota,
            paramaters_online,
            selectedTags: [],
          },
        });
      })
      .catch(error => {
        console.error(t('sendCodebook.error'), error);
        hideLoading();
      });
  };

  const sendEntitybook = (projectID, codeName, dataCount, question) => {
    showLoading();
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);
    fetch("https://codini-project-1-entity-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        hideLoading();
        navigate('/entity', {
          state: {
            updatedCategoryList: data.updated_category_list,
            projectID,
            codeName,
            dataCount,
            question,
            remainingQuota,
            selectedTags: [],
          },
        });
      })
      .catch(error => {
        console.error(t('sendEntitybook.error'), error);
        hideLoading();
      });
  };

  const sendAISegments = (projectID, codeName, dataCount, question) => {
    showLoading();
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);
    fetch("https://codini-project-1-profiling-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        hideLoading();
        navigate('/SegmentationPage', {
          state: {
            updatedCategoryList: data.updated_category_list,
            projectID,
            codeName,
            dataCount,
            question,
            remainingQuota,
            selectedTags: [],
          },
        });
      })
      .catch(error => {
        console.error(t('sendCodebook.error'), error);
        hideLoading();
      });
  };

  const AddDatatoProject = (projectID) => {
    showLoading();
    const formData = new FormData();
    const dataProcess = 'getColumn'
    formData.append('projectID', projectID);
    formData.append('dataProcess', dataProcess);
    fetch("https://codini-project-adddata-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        hideLoading();
        navigate('/addData', {
          state: {
            projectID,
            main_project_columns: data.main_project_columns,
            max_codini_id: data.max_codini_id
          },
        });
      })
      .catch(error => {
        console.error(t('sendResult.error'), error);
        hideLoading();
      });
  };

  const handleMasterButtonClick = (event, projectID, codeName, dataCount, question, masterExist, masterList, runCount) => {
    const masters = typeof masterList === 'string' ? masterList.split(',') : masterList;
    setMasterSelectionData({ projectID, codeName, dataCount, question, masterList: masters, runCount });
    setSelectedMaster(masters[0] || '');
    setSelectedCodebookId('');
    setBrandAlgorithm(false);
    setScanWithAI(false);
    setMasterAnchorEl(event.currentTarget);
  };

  const handleMasterConfirm = async () => {
    if (!masterSelectionData) return;
    setMasterAnchorEl(null);
    const { projectID, codeName, dataCount, question } = masterSelectionData;
    showLoading();
    
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);
    formData.append('question', question);
    
    // Seçilen kanal ve değeri belirle
    if (selectedCodebookId) {
      formData.append('master_channel', 'codeBookLibrary');
      formData.append('selected_codebook_id', selectedCodebookId);
    } else {
      formData.append('master_channel', 'masterSelection');
      formData.append('selected_master', selectedMaster);
    }
  
    formData.append('scan_with_ai', scanWithAI);

    try {
      const response = await fetch("https://codini-project-6-gt-795950092325.europe-west3.run.app", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      const paramaters_online= "frommaster"
      
      if (!brandAlgorithm) {
        navigate('/category', {
          state: {
            updatedCategoryList: data.updated_category_list,
            projectID,
            codeName,
            dataCount,
            question,
            remainingQuota,
            selectedTags: [],
            paramaters_online
          },
        });
      } else {
        navigate('/entity', {
          state: {
            updatedCategoryList: data.updated_category_list,
            projectID,
            codeName,
            dataCount,
            question,
            remainingQuota,
            selectedTags: [],
          },
        });
      }
    } finally {
      hideLoading();
    }
  };

  // Projeleri gruplandır
  const groupedProjects = () => {
    const projectGroups = {};
    filteredProjects.forEach(project => {
      if (!projectGroups[project.projectID]) {
        projectGroups[project.projectID] = {
          projectCode: project.projectCode,
          codingLanguage: project.codingLanguage,
          createDate: project.createDate,
          details: [],
        };
      }
      projectGroups[project.projectID].details.push(project);
    });
    return Object.values(projectGroups).sort((a, b) => {
      const aDate = new Date(a.createDate);
      const bDate = new Date(b.createDate);
      return bDate - aDate;
    });
  };

  // Grup genişletmeyi değiştir
  const toggleGroup = (projectCode) => {
    setExpandedGroups(prev => ({
      ...prev,
      [projectCode]: !prev[projectCode],
    }));
  };

  // Duruma göre projeleri filtrele
  const filterProjectsByProgress = (status) => {
    setFilterStatus(status);
    let filtered;
    if (status === 'normal') {
      filtered = projects.filter(project => ['New Record', 'Completed', 'In Progress', 'Error'].includes(project.progress));
    } else if (status === 'Archived') {
      filtered = projects.filter(project => project.progress === 'Archived');
    }
    setFilteredProjects(filtered);
  };

  // Arama işlemi
  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = projects.filter(project => project.projectCode.toLowerCase().includes(value));
    setFilteredProjects(filtered);
  };

  // Yenileme işlemi
  const handleRefresh = () => {
    showLoading();
    fetchProjectData().then(() => {
      hideLoading();
    });
  };

  // İlk veri çekimi
  useEffect(() => {
    fetchProjectData();
  }, []);

  useEffect(() => {
    let filtered = projects;
    if (filterStatus === 'normal') {
      filtered = filtered.filter(project => ['New Record', 'Completed', 'In Progress', 'Error'].includes(project.progress));
    } else if (filterStatus === 'Archived') {
      filtered = filtered.filter(project => project.progress === 'Archived');
    }
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.projectCode.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredProjects(filtered);
  }, [projects, filterStatus, searchTerm]);

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('loading')}
        </Typography>
      </Container>
    );
  }

  const getFinalReport = (projectID, projectCode) => {
    showLoading();
    const formData = new FormData();
    formData.append('projectID', projectID);
    
    fetch("https://codini-project-finalreporting-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData
    })
      .then(response => {
        if (!response.ok) throw new Error('Network error');
        return response.json();
      })
      .then(data => {
        navigate('/ReportDashboard', { 
          state: { 
            reportData: data.result_data,
            projectID: projectID,
            repospense_id: data.respospense_id,
            projectCode: projectCode 
          } 
        });
      })
      .catch(error => {
        console.error('Error:', error);
        setSnackbar({
          open: true,
          message: t('report.fetchError'),
          severity: 'error',
        });
      })
      .finally(() => hideLoading());
  };

  return (
    <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', padding: '20px' }}>
      <Container sx={{ mt: 1, backgroundColor: '#fff', borderRadius: 2, padding: '3px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
          {/* Yeni Proje Butonu */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '10px',
              backgroundColor: '#fff',
            }}
          >
            <Typography variant="h6" sx={{ mr: 2, fontSize: '14px' }}>
              {t('newProject')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/new-project')}
              sx={{
                padding: '5px 10px',
                fontSize: '12px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                backgroundColor: '#ff7b00',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#8ecae6',
                },
              }}
            >
              {t('verbatimCoding')}
            </Button>
          </Box>

          {/* Butonlar: Yenile, Normal, Arşiv */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Fab
              size="small"
              color="primary"
              onClick={handleRefresh}
              sx={{ width: 34, height: 34 }}
            >
              <RefreshIcon sx={{ fontSize: '1rem' }} />
            </Fab>
            <Fab
              size="small"
              color="secondary"
              onClick={() => setFilterStatus('normal')}
              sx={{ width: 34, height: 34 }}
            >
              <VisibilityIcon sx={{ fontSize: '1rem' }} />
            </Fab>
            <Fab
              size="small"
              color="primary"
              onClick={() => setFilterStatus('Archived')}
              sx={{ width: 34, height: 34 }}
            >
              <InventoryIcon sx={{ fontSize: '1rem' }} />
            </Fab>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {/* Kalan Kota */}
          {organisation !== 'sia' && remainingQuota !== null && (
            <Typography variant="body1" sx={{ backgroundColor: '#f0f0f0', padding: '8px', borderRadius: '4px', fontSize: '14px' }}>
              {t('remainingQuota')}: {remainingQuota}
            </Typography>
          )}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/LibraryPage', { state: { codeBookLibrary } })}
            sx={{
              mr: 1,
              padding: '5px 10px',
              fontSize: '12px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              backgroundColor: '#00a6fb',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#8ecae6'
              }
            }}
          >
            {t('codebookLibraryma')}
          </Button>

          {/* Arama Kutusu */}
          <TextField
            label={t('search')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            placeholder={t('projectCodeSearchPlaceholder')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: '300px' }}
          />
        </Box>

        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table id="projectsTable" sx={{ '& .MuiTableCell-root': { py: 0.5, borderBottom: '1px solid #e0e0e0' } }}>
            <TableHead sx={{ backgroundColor: '#f5f5f5', '& .MuiTableCell-root': { color: 'white', fontSize: '14px' } }}>
              <TableRow sx={{ backgroundColor: '#38a3a5' }}>
                <TableCell align="center">
                  <strong>{t('projectCode')}</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>{t('columnNumber')}</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>{t('question')}</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>{t('dataCount')}</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>{t('processStatus')}</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>{t('actions')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedProjects().length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('projectsPage.emptyMessage')}
                  </TableCell>
                </TableRow>
              ) : (
                groupedProjects().map((group, groupIndex) => {
                  const isExpanded = expandedGroups[group.projectCode];
                  const anyCompleted = group.details.some(project => project.progress === "Completed");
                  const anyprogress = group.details.some(project => project.progress === "In Progress");
                  const allArchivedAndRunCountGreaterThanZero = group.details.every(
                    project => project.progress === "Archived" && project.runCount > 0
                  );
                  const finalFileButtonDisabled = !(anyCompleted || allArchivedAndRunCountGreaterThanZero);
                  const adddataButtonDisabled = (anyprogress);
                  const archiveButtonDisabled = group.details[0].progress === "Archived";

                  return (
                    <React.Fragment key={groupIndex}>
                      <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                        <TableCell sx={{ backgroundColor: '#b7e4c7' }} colSpan={6}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton onClick={() => toggleGroup(group.projectCode)} size="small">
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                              <Typography variant="subtitle1" component="span" sx={{ fontStyle: 'bold' }}>
                                {group.projectCode} -{' '}
                                <Typography variant="body2" component="span">
                                  {t('languageLabel')}: {group.codingLanguage} {t('createDateLabel')} {new Date(group.createDate).toLocaleDateString()}
                                </Typography>
                              </Typography>
                            </div>
                            <div>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => getFile(group.details[0].projectID)}
                                sx={{
                                  mr: 1,
                                  padding: '5px 10px',
                                  fontSize: '9px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                  backgroundColor: '#ffb703',
                                  color: '#fff',
                                  '&:hover': {
                                    backgroundColor: '#8ecae6'
                                  },
                                  '&.Mui-disabled': {
                                    backgroundColor: '#b0b0b0',
                                    color: '#666',
                                    cursor: 'not-allowed'
                                  }
                                }}
                              >
                                {t('rawFile')}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  const projectDate = new Date(group.createDate);
                                  const cutoffDate = new Date('2025-03-18T00:00:00');
                                  if (projectDate >= cutoffDate) {
                                    AddDatatoProject(group.details[0].projectID);
                                  } else {
                                    setSnackbar({
                                      open: true,
                                      message: t('finalReportwarningMessage'),
                                      severity: 'warning',
                                    });
                                  }
                                }}
                                disabled={adddataButtonDisabled}
                                sx={{
                                  mr: 1,
                                  padding: '5px 10px',
                                  fontSize: '9px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                  backgroundColor: adddataButtonDisabled ? '#ccc' : '#f7a072',
                                  color: '#fff',
                                  '&:hover': {
                                    backgroundColor: adddataButtonDisabled ? '#ccc' : '#8ecae6'
                                  },
                                  '&.Mui-disabled': {
                                    border: 'none',
                                    backgroundColor: '#b0b0b0',
                                    color: '#666',
                                    cursor: 'not-allowed'
                                  }
                                }}
                              >
                                {t('addData')}
                              </Button>  
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => getFinalFile(group.details[0].projectID)}
                                disabled={finalFileButtonDisabled}
                                sx={{
                                  mr: 1,
                                  padding: '5px 10px',
                                  fontSize: '9px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                  backgroundColor: finalFileButtonDisabled ? '#ccc' : '#fb8500',
                                  color: '#fff',
                                  '&:hover': {
                                    backgroundColor: finalFileButtonDisabled ? '#ccc' : '#8ecae6'
                                  },
                                  '&.Mui-disabled': {
                                    border: 'none',
                                    backgroundColor: '#b0b0b0',
                                    color: '#666',
                                    cursor: 'not-allowed'
                                  }
                                }}
                              >
                                {t('finalFile')}
                              </Button>
                                                                  
                            </div>
                            <div>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  const projectDate = new Date(group.createDate);
                                  const cutoffDate = new Date('2025-03-18T00:00:00');
                                  if (projectDate >= cutoffDate) {
                                    getFinalReport(group.details[0].projectID, group.details[0].projectCode);
                                  } else {
                                    setSnackbar({
                                      open: true,
                                      message: t('finalReportwarningMessage'),
                                      severity: 'warning',
                                    });
                                  }
                                }}
                                disabled={finalFileButtonDisabled}
                                sx={{
                                  padding: '5px 10px',
                                  fontSize: '9px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                  backgroundColor: finalFileButtonDisabled ? '#ccc' : '#780000',
                                  color: '#fff',
                                  '&:hover': {
                                    backgroundColor: finalFileButtonDisabled ? '#ccc' : '#8ecae6'
                                  },
                                  '&.Mui-disabled': {
                                    border: 'none',
                                    backgroundColor: '#b0b0b0',
                                    color: '#666',
                                    cursor: 'not-allowed'
                                  }
                                }}
                              >
                                {t('finalReport')}
                              </Button>
                            
                              <IconButton variant="outlined" color="error" size="small" sx={{ mr: 1 , ml:2}} onClick={() => archiveProject(group.details[0].projectID, 'Archived')} disabled={archiveButtonDisabled}>
                                <InventoryIcon fontSize="small" />
                              </IconButton>
                              <IconButton variant="outlined" color="error" size="small" onClick={() => archiveProject(group.details[0].projectID, 'Deleted')}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && group.details.map((project, detailIndex) => {
                        const masterOrRunExists = group.details.some(p => p.masterExist === 1 || p.runCount > 0);
                        const masterOrRunExistsCodeNames = group.details
                          .filter(p => p.masterExist === 1 || p.runCount > 0)
                          .map(p => p.codeName);
                        let resultButtonDisabled = false;
                        let codebookButtonDisabled = false;
                        let mastarButtonDisabled = false;
                        if (
                          project.progress === "New Record" ||
                          project.progress === "In Progress" ||
                          project.progress === "Error" ||
                          project.runCount === 0
                        ) {
                          resultButtonDisabled = true;
                        }
                        if (
                          project.progress === "Archived" ||
                          project.progress === "In Progress" ||
                          project.masterExist === 1
                        ) {
                          codebookButtonDisabled = true;
                        }
                        if (!masterOrRunExists || project.progress === "Archived" || project.progress === "In Progress") {
                          mastarButtonDisabled = true;
                        }
                        return (
                          <TableRow
                            key={detailIndex}
                            data-progress={project.progress}
                            sx={{
                              fontSize: '12px',
                              backgroundColor: detailIndex % 2 === 0 ? '#f9f9f9' : '#ffffff',
                              '&:hover': { backgroundColor: '#f5f5f5' },
                            }}
                          >
                            <TableCell></TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{project.codeName}</TableCell>
                            <TableCell sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              width: '30%',
                              maxWidth: '20vw',
                            }}>
                              <Tooltip title={project.question}>
                                <span>{project.question}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">{project.dataCount}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {project.progress === 'Completed' && (
                                  <FiberManualRecordIcon sx={{ color: 'green', fontSize: 'small', mr: 1 }} />
                                )}
                                {project.progress === 'New Record' && (
                                  <FiberManualRecordIcon sx={{ color: 'yellow', fontSize: 'small', mr: 1 }} />
                                )}
                                {project.progress === 'In Progress' && (
                                  <FiberManualRecordIcon sx={{ color: 'red', fontSize: 'small', mr: 1 }} />
                                )}
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{project.progress}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title={t('themes')}>
                                  <span>
                                    <IconButton
                                      variant="outlined"
                                      sx={{
                                        mr: 1,
                                        padding: '5px 10px',
                                        fontSize: '9px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        backgroundColor: codebookButtonDisabled ? '#ccc' : '#4895ef',
                                        color: '#fff',
                                        '&:hover': {
                                          backgroundColor: codebookButtonDisabled ? '#ccc' : '#8ecae6'
                                        },
                                        '&.Mui-disabled': {
                                          backgroundColor: '#b0b0b0',
                                          color: '#666',
                                          cursor: 'not-allowed'
                                        }
                                      }}
                                      onClick={() => sendCodebook(project.projectID, project.codeName, project.dataCount, project.question)}
                                      disabled={codebookButtonDisabled}
                                    >
                                      {t('themes')}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t('entity')}>
                                  <span>
                                    <IconButton
                                      variant="outlined"
                                      sx={{
                                        mr: 1,
                                        padding: '5px 10px',
                                        fontSize: '9px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        backgroundColor: codebookButtonDisabled ? '#ccc' : '#4895ef',
                                        color: '#fff',
                                        '&:hover': {
                                          backgroundColor: codebookButtonDisabled ? '#ccc' : '#8ecae6'
                                        },
                                        '&.Mui-disabled': {
                                          backgroundColor: '#b0b0b0',
                                          color: '#666',
                                          cursor: 'not-allowed'
                                        }
                                      }}
                                      onClick={() => sendEntitybook(project.projectID, project.codeName, project.dataCount, project.question)}
                                      disabled={codebookButtonDisabled}
                                    >
                                      {t('entity')}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t('master')}>
                                  <span>
                                    <IconButton
                                      variant="outlined"
                                      sx={{
                                        mr: 1,
                                        padding: '5px 10px',
                                        fontSize: '9px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        backgroundColor: mastarButtonDisabled ? '#ccc' : '#4895ef',
                                        color: '#fff',
                                        '&:hover': {
                                          backgroundColor: mastarButtonDisabled ? '#ccc' : '#8ecae6'
                                        },
                                        '&.Mui-disabled': {
                                          backgroundColor: '#b0b0b0',
                                          color: '#666',
                                          cursor: 'not-allowed'
                                        }
                                      }}
                                      onClick={(e) =>
                                        handleMasterButtonClick(e, project.projectID, project.codeName, project.dataCount, project.question, project.masterExist, masterOrRunExistsCodeNames, project.runCount)
                                      }
                                      disabled={mastarButtonDisabled}
                                    >
                                      {t('master')}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t('AISegments')}>
                                  <span>
                                    <IconButton
                                      variant="outlined"
                                      sx={{
                                        mr: 1,
                                        padding: '5px 10px',
                                        fontSize: '9px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        backgroundColor: codebookButtonDisabled ? '#ccc' : '#1a659e',
                                        color: '#fff',
                                        '&:hover': {
                                          backgroundColor: codebookButtonDisabled ? '#ccc' : '#8ecae6'
                                        },
                                        '&.Mui-disabled': {
                                          backgroundColor: '#b0b0b0',
                                          color: '#666',
                                          cursor: 'not-allowed'
                                        }
                                      }}
                                      onClick={() => sendAISegments(project.projectID, project.codeName, project.dataCount, project.question)}
                                      disabled={codebookButtonDisabled}
                                    >
                                      {t('AISegments')}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t('dashboardname')}>
                                  <span>
                                    <IconButton
                                      variant="outlined"
                                      sx={{
                                        mr: 1,
                                        padding: '5px 10px',
                                        fontSize: '9px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        backgroundColor: resultButtonDisabled ? '#edede9' : '#480ca8',
                                        color: '#fff',
                                        '&:hover': {
                                          backgroundColor: resultButtonDisabled ? '#ccc' : '#8ecae6'
                                        },
                                        '&.Mui-disabled': {
                                          backgroundColor: '#b0b0b0',
                                          color: '#666',
                                          cursor: 'not-allowed'
                                        }
                                      }}
                                      onClick={() => sendResult(project.projectID, project.codeName, project.dataCount, project.question, project.masterExist)}
                                      disabled={resultButtonDisabled}
                                    >
                                      {t('dashboardname')}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        {apiLoading && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
          >
            <CircularProgress />
          </div>
        )}

        <Popover
          open={Boolean(masterAnchorEl)}
          anchorEl={masterAnchorEl}
          onClose={() => setMasterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Paper sx={{ p: 2, width: 300 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('masterSelection')}
            </Typography>
            
            {/* Master Selection */}
            <FormControl fullWidth sx={{ mb: 2 }} size="small">
              <InputLabel id="master-select-label">
                {t('projectCodebooks')}
              </InputLabel>
              <Select
                labelId="master-select-label"
                value={selectedMaster}
                label={t('projectCodebooks')}
                onChange={(e) => {
                  setSelectedMaster(e.target.value);
                  setSelectedCodebookId('');
                }}
              >
                {masterSelectionData && masterSelectionData.masterList.map((master, index) => (
                  <MenuItem key={index} value={master}>{master}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Codebook Selection */}
            {codeBookLibrary && (
              <FormControl fullWidth sx={{ mb: 2 }} size="small">
                <InputLabel id="codebook-select-label">
                  {t('codebookLibraryma')}
                </InputLabel>
                <Select
                  labelId="codebook-select-label"
                  value={selectedCodebookId}
                  label={t('codebookLibraryma')}
                  onChange={(e) => {
                    setSelectedCodebookId(e.target.value);
                    setSelectedMaster('');
                  }}
                >
                  {codeBookLibrary.codebooks.map((cb) => (
                    <MenuItem key={cb.id} value={cb.id}>
                      {cb.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={brandAlgorithm}
                    onChange={(e) => setBrandAlgorithm(e.target.checked)}
                    size="small"
                  />
                }
                label={t('useBrandMatch')}
              />
            </Box> 
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={scanWithAI}
                    onChange={(e) => setScanWithAI(e.target.checked)}
                    size="small"
                  />
                }
                label={t('scanDataWithAI')}
              />
            </Box>
            <Box sx={{ textAlign: 'right', mt: 2 }}>
              <Button variant="contained" size="small" onClick={handleMasterConfirm}>
                {t('confirm')}
              </Button>
            </Box>
          </Paper>
        </Popover>
      </Container>
    </Box>
  );
}